/* Photo report — the review grid.
   Visual reference: docs/design/photo-review.html. Normative rules: docs/design/spec/.

   Shape: filters card → status/view segments → photo grid → sticky bulk bar → review modal.
   Filters apply as they change (there is no Apply button in the design); the free-text comment
   field is debounced so typing costs one request, not one per keystroke.

   Two things the API cannot do, and how this page copes:
   - `visits/photos_from_reports/` proxies Outforz and joins picsell approvals onto the page it
     got back, so it cannot FILTER by approval status. The status segment therefore narrows the
     rows currently LOADED, and this page states no total at all: the endpoint's own `count` is
     Outforz's and the verdicts are picsell's, so any number over the whole filtered set needed
     an extra join across both (`photo_report_status_counts`, since deleted) and every count on
     the screen would have been either a page-local number reading as a total or a total that
     quietly ignored an Outforz-only filter.
     ponytail: client-side status filter, bounded by MAX_MOUNTED_ITEMS. Needs an
     `is_approved` query param on the endpoint to become a real server-side filter.
   - A shared URL carries only what the endpoint matches on: names for categories / brands / tags,
     a code for products, ids for points and merchandisers. The first two are self-describing, so
     their chips read as words straight away; the id-matched ones are named by `EntitySelect`'s
     `onResolve` once it has fetched them, into the `labels` cache. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Avatar,
  Button,
  Card,
  Carousel,
  Checkbox,
  Col,
  Empty,
  Flex,
  Form,
  Image,
  Input,
  Popconfirm,
  Row,
  Segmented,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ApartmentOutlined,
  AppstoreOutlined,
  BookOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  ClusterOutlined,
  CompassOutlined,
  CopyOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownOutlined,
  DownloadOutlined,
  FileImageOutlined,
  IdcardOutlined,
  InboxOutlined,
  LeftOutlined,
  PictureOutlined,
  PrinterOutlined,
  PushpinOutlined,
  RightOutlined,
  SaveOutlined,
  SearchOutlined,
  ShopOutlined,
  SlidersOutlined,
  TagOutlined,
  TagsOutlined,
  ZoomInOutlined,
} from "@ant-design/icons";

import Alert from "/src/util/alert";

import EntitySelect from "../../component/EntitySelect";
import FilterChips, { useLabelCache } from "../../component/FilterChips";
import PeriodPicker, { clampRange, defaultRange } from "../../component/PeriodPicker";
import { ENTITY_DROPDOWN_SETTINGS, PHOTO_REPORT, PHOTO_REVIEW, SIDE_NAV, TRANSLATION_DOWNLOADER } from "../../config/lang";
import { PhotoReview } from "../../models/photo-review";
import { call, getAbortable } from "../../util/action";
import dayjs from "../../util/dayjs";
import { regionId, translate } from "../../util/util";
import { localeFor } from "../merchandiser/route/dateUtils";

import RejectTaskModal from "../../component/RejectTaskModal";
import ReviewModal from "../../component/ReviewModal";
import { StatusPill, fill, fullName, initialsOf, statusOf } from "./parts";
import "./photo-review.scss";

/* Caps how many infinite-list rows stay mounted at once. Carried over from the previous
   implementation: without it the DOM grows for as long as the session lasts. */
const MAX_MOUNTED_ITEMS = 100;

/**
 * The widest period this page will query, in calendar months.
 *
 * `photos_from_reports` proxies Outforz per request and cannot be narrowed by approval status, so
 * the date window is the only thing bounding how much it fetches. The cap is enforced in three
 * places, because there are three ways a range gets in: the calendar greys out days beyond it,
 * `searchToFilter` clamps a pasted URL, and `buildQuery` clamps whatever is in state at request
 * time — the last one is the backstop that makes the other two convenience rather than security.
 */
const MAX_RANGE_DAYS = 90;

/* One request per pause in filtering, not one per click (spec/03). The comment box keeps its own
   timer of the same length so a keystroke does not also rewrite the address bar. */
const FILTER_DEBOUNCE_MS = 250;

const STATUS_SEGMENTS = [
  ["all", "status_all"],
  ["pending", "status_pending"],
  ["approved", "status_approved"],
  ["rejected", "status_rejected"],
];

/* Cards: 1 / 2 / 3 / 4 / 6 across, on antd's 24-column grid per spec/02. */
const CARD_COL = { xs: 24, sm: 12, md: 8, lg: 6, xxl: 4 };

/* The filter row is five equal tracks with the period spanning two, and 24 does not divide by
   five. `<Col>`'s responsive value may be an object rather than a span, and that object takes
   `flex` — so the track width is expressed as a percentage instead. It lands on the design's
   210px because antd implements `<Row gutter>` as padding INSIDE each Col (with a negative margin
   on the Row), so `0 0 20%` is a fifth of the row and the 12px gap comes out of it.
   `0 0` rather than `1 1`: a last row of three must leave two empty tracks, not stretch. */
const FIELD_COL = { xs: { flex: "0 0 100%" }, sm: { flex: "0 0 50%" }, md: { flex: "0 0 33.3333%" }, lg: { flex: "0 0 20%" } };
const FIELD_COL_WIDE = { xs: { flex: "0 0 100%" }, sm: { flex: "0 0 100%" }, md: { flex: "0 0 66.6667%" }, lg: { flex: "0 0 40%" } };

const blankFilter = () => ({
  county: "",
  clients: "",
  point__network: "",
  points: [],
  users: [],
  categories: [],
  subcategories: [],
  brands: [],
  products: [],
  tags: [],
  comment: "",
});

/* Which chips a filter key produces, and where its label comes from. Single-value keys read
   their label from `chosen` (the option object the select handed back); array keys carry their
   own objects. */
const CHIP_LABEL = {
  county: "region",
  clients: "clients",
  point__network: "network",
  points: "points",
  users: "merchandisers",
  categories: "categories",
  subcategories: "subcategories",
  brands: "brands",
  products: "products",
  tags: "tags",
  comment: "comment",
};

const SINGLE_KEYS = ["county", "clients", "point__network"];
const ARRAY_KEYS = ["points", "users", "categories", "subcategories", "brands", "products", "tags"];
/**
 * The array filters the endpoint matches by NAME rather than by id (see `buildQuery`).
 *
 * Their URL value has to be the name too. Round-tripping them by id — which is what this used to
 * do — meant `joinNames` got a `{ id: 5 }` with no `name` on it after a reload, dropped the value,
 * and the filter silently stopped applying while its chip still showed. Products are the same
 * problem a third way: they match by `code`.
 */
const NAME_KEYS = ["categories", "subcategories", "brands", "tags"];

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);
const nameOf = v => (v && typeof v === "object" ? v.label || v.name : v);
const joinIds = list =>
  (list || [])
    .map(idOf)
    .filter(v => v !== undefined && v !== null && v !== "")
    .join(",");
const joinNames = list =>
  (list || [])
    .map(nameOf)
    .filter(Boolean)
    .join(",");
const joinCodes = list =>
  (list || [])
    .map(o => o?.code)
    .filter(Boolean)
    .join(",");

const isoDay = d => dayjs(d).format("YYYY-MM-DD");

/**
 * The request the grid runs on.
 *
 * Built here rather than through `filterUrl()` so the mapping is visible in one place: each
 * value has to arrive in the shape the Outforz proxy expects — ids for merchandisers and
 * points, names for categories / subcategories / brands / tags, product *codes* for products.
 * `end__lte` is the selected end date plus a day, which is what makes the range inclusive.
 */
function buildQuery({ filter, range, page, viewMode }) {
  const p = new URLSearchParams();
  p.set("page", String(page));

  if (filter.county) p.set("county", String(idOf(filter.county)));
  if (filter.clients) p.set("clients", String(idOf(filter.clients)));
  if (filter.point__network) p.set("point__network", String(idOf(filter.point__network)));
  if (filter.points?.length) p.set("points", joinIds(filter.points));
  if (filter.users?.length) p.set("users", joinIds(filter.users));
  if (filter.categories?.length) p.set("categories", joinNames(filter.categories));
  if (filter.subcategories?.length) p.set("subcategories", joinNames(filter.subcategories));
  if (filter.brands?.length) p.set("brands", joinNames(filter.brands));
  if (filter.tags?.length) p.set("tags", joinNames(filter.tags));
  if (filter.products?.length) p.set("products", joinCodes(filter.products));
  if (filter.comment) p.set("comment", filter.comment);

  // The backend only applies a date window when it gets both ends.
  const window = clampRange(range, MAX_RANGE_DAYS);
  if (window?.[0] && window?.[1]) {
    p.set("start__gte", isoDay(window[0]));
    p.set("end__lte", isoDay(dayjs(window[1]).add(1, "day")));
  }
  if (viewMode === "group") p.set("report_type", "grouped");

  return p.toString();
}

/** URL ↔ filter sync, so a filtered view can be shared and survives a reload. */
function filterToSearch(filter, range) {
  const p = new URLSearchParams();
  if (range?.[0]) p.set("start", isoDay(range[0]));
  if (range?.[1]) p.set("end", isoDay(range[1]));
  SINGLE_KEYS.forEach(key => {
    if (filter[key]) p.set(key, String(idOf(filter[key])));
  });
  ARRAY_KEYS.forEach(key => {
    (filter[key] || []).forEach(v => {
      /* Whatever the endpoint matches on — see NAME_KEYS. The URL carries the same token the query
         will, so a reload reconstructs a working filter rather than an id the query cannot use. */
      const token = key === "products" ? v?.code : NAME_KEYS.includes(key) ? nameOf(v) : idOf(v);
      if (token !== undefined && token !== null && token !== "") p.append(key, String(token));
    });
  });
  if (filter.comment) p.set("comment", filter.comment);
  return p.toString();
}

function searchToFilter(search) {
  const p = new URLSearchParams(search);
  const filter = blankFilter();
  SINGLE_KEYS.forEach(key => {
    if (p.get(key)) filter[key] = p.get(key);
  });
  ARRAY_KEYS.forEach(key => {
    const values = p.getAll(key);
    if (!values.length) return;
    /* A name-matched filter is self-describing, so its chip reads as words with no lookup. An
       id-matched one (points, merchandisers) is not: `EntitySelect`'s `onResolve` names those. */
    filter[key] = values.map(v =>
      key === "products" ? { code: v, id: v, name: v } : NAME_KEYS.includes(key) ? { id: v, name: v } : { id: /^\d+$/.test(v) ? Number(v) : v }
    );
  });
  if (p.get("comment")) filter.comment = p.get("comment");
  const start = p.get("start");
  const end = p.get("end");
  /* A pasted or bookmarked URL is untrusted input like any other: clamp it to the cap rather than
     trusting the address bar, and fall back to the default window if it carries no period at all
     (or only one end, which is not a range). */
  const range = start && end ? clampRange([dayjs(start), dayjs(end)], MAX_RANGE_DAYS) : defaultRange();
  return { filter, range };
}

/* -------------------------------------------------------------------- pieces */

/**
 * The card cover: `<Carousel>` for the photos, `<Image>` for each one.
 *
 * The carousel owns the index — that is why `photoIndex` / `onRoll` / `onDot` are gone from the
 * page. It reports back through `afterChange` only so the `n/N` tag can say where it is; nothing
 * outside this component reads the value, so there is no state to keep in sync.
 *
 * `<Image preview={false}>`: the cover's job is to open the review modal, and antd's own preview
 * overlay would intercept the click that does it.
 */
function PhotoCover({ item, lang, isSelected, busy, onToggle, onOpen, onApprove, onReject }) {
  const t = key => translate(PHOTO_REPORT, key, lang);
  const photos = item.photo || [];
  const many = photos.length > 1;
  const status = statusOf(item);
  const carousel = useRef(null);
  const [index, setIndex] = useState(0);

  /* Only the cover image opens the review modal; every control on top of it stops the click. */
  const swallow = fn => event => {
    event.stopPropagation();
    fn();
  };

  return (
    <Flex className="pr-cover" onClick={onOpen}>
      {photos.length ? (
        <Carousel
          ref={carousel}
          /* `className` lands on the inner slick slider; the outer `.ant-carousel` — the one that
             has to fill the cover — is only reachable through `rootClassName`. */
          rootClassName="pr-cover__reel"
          dots={many}
          infinite={false}
          /* A grouped report can hold a dozen photos and slick renders every slide, so without
             this the card fetched all of them at full size to show one. */
          lazyLoad="ondemand"
          /* Slide changes are a side effect of the arrows below, which already stop the click. */
          afterChange={setIndex}>
          {photos.map((src, i) => (
            <Image
              key={`${src}-${i}`}
              className="pr-cover__img"
              src={src}
              alt=""
              preview={false}
              rootClassName="pr-cover__imgwrap"
              /* These are full-resolution shelf photos — there is no thumbnail endpoint — and a
                 page is 20 cards. `loading="lazy"` is what keeps a page from opening 20 of them
                 at once through a 6-connection budget. The cover's box is fixed in CSS
                 (`aspect-ratio` + `object-fit`), so nothing shifts as they arrive. */
              loading="lazy"
            />
          ))}
        </Carousel>
      ) : (
        <Flex className="pr-cover__none" vertical align="center" justify="center" gap={4}>
          <FileImageOutlined className="pr-cover__none-icon" />
          {t("no_photo")}
        </Flex>
      )}

      <StatusPill status={status} lang={lang} />

      <Flex className="pr-cover__check" align="center" justify="center" onClick={event => event.stopPropagation()}>
        <Checkbox checked={isSelected} onChange={onToggle} aria-label={t("a_select")} />
      </Flex>

      <Space className="pr-cover__acts" size={4}>
        <Button className="pr-actbtn" size="small" icon={<ZoomInOutlined />} aria-label={t("review_title")} onClick={swallow(onOpen)} />
        <Button
          className={`pr-actbtn pr-actbtn--no${status === "rejected" ? " pr-actbtn--on" : ""}`}
          size="small"
          loading={busy}
          icon={<CloseCircleOutlined />}
          aria-label={translate(PHOTO_REVIEW, "reject_btn", lang)}
          onClick={swallow(onReject)}
        />
        <Button
          className={`pr-actbtn pr-actbtn--ok${status === "approved" ? " pr-actbtn--on" : ""}`}
          size="small"
          loading={busy}
          icon={<CheckCircleOutlined />}
          aria-label={translate(PHOTO_REVIEW, "approv_btn", lang)}
          onClick={swallow(onApprove)}
        />
      </Space>

      {many && (
        <>
          <Tag variant="filled" icon={<PictureOutlined />} className="pr-countpill">
            {index + 1}/{photos.length}
          </Tag>
          {/* Carousel's own `arrows` render inside the reel, under the overlays and without an
              accessible name; these drive it through its ref instead. */}
          <Flex className="pr-cover__nav" align="center" justify="space-between">
            <Button
              className="pr-navbtn"
              shape="circle"
              size="small"
              icon={<LeftOutlined />}
              aria-label={t("a_prev_photo")}
              onClick={swallow(() => carousel.current?.prev())}
            />
            <Button
              className="pr-navbtn"
              shape="circle"
              size="small"
              icon={<RightOutlined />}
              aria-label={t("a_next_photo")}
              onClick={swallow(() => carousel.current?.next())}
            />
          </Flex>
        </>
      )}
    </Flex>
  );
}

/**
 * The card's tag row: one line, scrolled sideways, never wrapped.
 *
 * A wrapping row made the card grow by a line for every extra tag, so a grid of cards with uneven
 * tag counts had uneven heights. This keeps the row one line tall whatever it holds, and says how
 * many tags are off-screen with a `+N` pinned to the right edge.
 *
 * `+N` hides while the row is being scrolled, because during a scroll it is both wrong (the count
 * is changing under the finger) and in the way of what the user is scrolling toward. It comes back
 * ~400ms after the last scroll event, recounted.
 */
function TagRow({ tags }) {
  const ref = useRef(null);
  const idle = useRef(null);
  const [hidden, setHidden] = useState(0);
  const [scrolling, setScrolling] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    /* Count what starts past the right edge rather than what merely straddles it: a half-visible
       tag is legible, and counting it made `+N` say there was more than there was. */
    const edge = el.scrollLeft + el.clientWidth;
    setHidden([...el.children].filter(child => child.offsetLeft + child.offsetWidth > edge + 1).length);
  }, []);

  useEffect(() => {
    measure();
    /* The card is in a responsive grid, so the row's width changes without any scroll or
       re-render — a resize is the only signal that the count is stale. */
    const observer = new ResizeObserver(measure);
    if (ref.current) observer.observe(ref.current);
    return () => {
      observer.disconnect();
      clearTimeout(idle.current);
    };
  }, [measure, tags]);

  const onScroll = () => {
    measure();
    setScrolling(true);
    clearTimeout(idle.current);
    idle.current = setTimeout(() => setScrolling(false), 400);
  };

  return (
    <Flex className="pr-tagrow">
      <Flex ref={ref} className="pr-tagrow__scroll" gap={4} onScroll={onScroll}>
        {tags.map(tag => (
          <Tag variant="filled" className="pr-tag" key={tag}>
            {tag}
          </Tag>
        ))}
      </Flex>
      {hidden > 0 && !scrolling && (
        <Tag variant="filled" className="pr-tagrow__more" title={tags.slice(tags.length - hidden).join(", ")}>
          +{hidden}
        </Tag>
      )}
    </Flex>
  );
}

/* A page is up to MAX_MOUNTED_ITEMS cards, each with a slick carousel and a ResizeObserver, so
   an unmemoised card meant one keystroke in the comment box re-rendered the whole grid. The
   parent's `on*` handlers take the item and are `useCallback`-stable for this to hold; the
   zero-arg wrappers the cover wants are built here, where they cost nothing. */
const PhotoCard = React.memo(function PhotoCard(props) {
  const { item, lang, grouped, isSelected, onToggle, onOpen, onApprove, onReject } = props;
  const info = item.additional_info || {};
  const tags = Array.isArray(info.tags) ? info.tags : [];
  const point = item.visit?.point;
  const user = fullName(item.visit?.user);
  const photos = item.photo || [];

  const open = () => onOpen(item);
  const cover = (
    <PhotoCover
      item={item}
      lang={lang}
      grouped={grouped}
      isSelected={isSelected}
      busy={props.busy}
      onToggle={() => onToggle(item)}
      onOpen={open}
      onApprove={() => onApprove(item)}
      onReject={() => onReject(item)}
    />
  );

  const since = item.visit?.start ? relativeFormat(lang).format(...relativeParts(new Date(item.visit.start))) : "";

  return (
    <Card
      hoverable
      className={`pr-card${grouped ? " pr-card--group" : ""}${isSelected ? " pr-card--selected" : ""}`}
      cover={cover}
      styles={{ body: { padding: 0 } }}>
      <Flex vertical gap="small" className="pr-card__body" onClick={open}>
        <Flex vertical>
          <Typography.Text type="secondary" className="pr-card__loc">
            {[point?.locality?.name, point?.network?.name].filter(Boolean).join(" · ")}
          </Typography.Text>
          {/* `ellipsis` measures the rendered text, so the title attribute is antd's job now — a
              hand-written one repeated the label even when it fitted. */}
          <Typography.Text strong ellipsis={{ tooltip: point?.name || "" }} className="pr-card__pt">
            {point?.name}
          </Typography.Text>
        </Flex>
        {tags.length > 0 && <TagRow tags={tags} />}
        <Flex align="center" gap="small" className="pr-card__foot">
          <Flex align="center" gap={4} className="pr-card__who">
            <Avatar size={24} className="pr-card__av">
              {initialsOf(user)}
            </Avatar>
            <Typography.Text ellipsis>{user}</Typography.Text>
          </Flex>
          {grouped ? (
            <Tooltip title={translate(PHOTO_REPORT, "photos_label", lang)}>
              <Typography.Text className="pr-card__time">
                <PictureOutlined className="pr-card__count-icon" /> {photos.length}
              </Typography.Text>
            </Tooltip>
          ) : (
            <Typography.Text className="pr-card__time">{since}</Typography.Text>
          )}
        </Flex>
      </Flex>
    </Card>
  );
});

/* One formatter per locale rather than one per card per render. */
const RELATIVE_FORMATS = new Map();

function relativeFormat(lang) {
  const locale = localeFor(lang);
  if (!RELATIVE_FORMATS.has(locale)) {
    RELATIVE_FORMATS.set(locale, new Intl.RelativeTimeFormat(locale, { numeric: "auto" }));
  }
  return RELATIVE_FORMATS.get(locale);
}

/** `Intl.RelativeTimeFormat` args for "11 d ago" — the largest unit that isn't zero. */
function relativeParts(date) {
  const minutes = Math.max(Math.round((Date.now() - date.getTime()) / 60000), 0);
  if (minutes < 60) return [-minutes, "minute"];
  if (minutes < 1440) return [-Math.round(minutes / 60), "hour"];
  return [-Math.round(minutes / 1440), "day"];
}

/* The same `<Card cover>` the real thing uses, so the placeholder cannot drift from it. */
function CardSkeleton() {
  return (
    <Card className="pr-card pr-sk" cover={<Skeleton.Image active />} styles={{ body: { padding: "var(--space-3)" } }}>
      <Skeleton active title={false} paragraph={{ rows: 2 }} />
    </Card>
  );
}

/* ---------------------------------------------------------------------- page */

export default function PhotoReportPage() {
  const me = useSelector(state => state.me);
  const lang = me?.language || "en";
  const history = useHistory();
  const location = useLocation();
  const { id: routeId } = useParams();

  const t = (key, group = PHOTO_REPORT) => translate(group, key, lang);

  /* Read once, on the first render: after that the address bar is written FROM this state, so
     re-reading it would fight the write. */
  const initialRef = useRef(null);
  if (!initialRef.current) {
    initialRef.current =
      location.search && location.search.length > 1
        ? searchToFilter(location.search)
        : { filter: { ...blankFilter(), county: regionId(me) || "" }, range: defaultRange() };
  }
  const initial = initialRef.current;

  const [filter, setFilter] = useState(initial.filter);
  /* Labels for the single-value selects, so their chips read as words. The filter itself keeps
     ids, which is what the endpoint wants. */
  const [chosen, setChosen] = useState(() => (me?.county && typeof me.county === "object" ? { county: me.county } : {}));
  const [range, setRangeState] = useState(initial.range);
  /**
   * The only way the period changes. Everything routes through here — the picker, the chip's ×,
   * a saved preset, Reset — so "the state never holds a window wider than the cap, and never
   * holds none" is an invariant rather than a rule each call site has to remember.
   *
   * The picker clamps too, but antd commits a range on blur and on Esc as well as on a completed
   * pick, and those paths do not all pass through the same handler. Clamping here means it does
   * not matter which one fired.
   */
  const setRange = next => setRangeState(clampRange(next, MAX_RANGE_DAYS) || defaultRange());

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const page = useRef(1);

  const [viewMode, setViewMode] = useState("single");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showExtra, setShowExtra] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [busyId, setBusyId] = useState(null);
  /** `{ item, advance }` while the reject-and-raise-a-task modal is open. */
  const [rejecting, setRejecting] = useState(null);
  const [openId, setOpenId] = useState(routeId ? Number(routeId) : null);

  const [presetName, setPresetName] = useState("");
  const [presetVersion, setPresetVersion] = useState(0);
  const [preset, setPreset] = useState(null);

  const [exporting, setExporting] = useState(false);

  /* One in-flight request at a time, via the same `getAbortable` every other list in the app
     uses. A `seq` ref used to drop the stale RESPONSE, which is not the same thing: the socket
     stayed open and the upstream query kept running, so fiddling with filters stacked several
     of the slowest query in the product. Aborting frees the browser's connection slot for the
     images — it does not stop a query already executing on the server. */
  const inflight = useRef(null);

  const fetchPage = useCallback(
    (nextPage, append) => {
      inflight.current?.abort();
      const controller = new AbortController();
      inflight.current = controller;

      const url = `visits/photos_from_reports/?${buildQuery({ filter, range, page: nextPage, viewMode })}`;
      append ? setLoadingMore(true) : setLoading(true);

      getAbortable(url, controller.signal)
        .then(response => {
          if (!response?.results) {
            setLoading(false);
            setLoadingMore(false);
            return;
          }
          const rows = response.results.map(row => new PhotoReview(row));
          page.current = nextPage;
          // `report_type=grouped` answers without a `next` key; infer from an empty page there.
          setHasNext(response.next !== undefined ? Boolean(response.next) : rows.length > 0);
          setData(prev => {
            const merged = append ? [...prev, ...rows] : rows;
            return merged.length > MAX_MOUNTED_ITEMS ? merged.slice(-MAX_MOUNTED_ITEMS) : merged;
          });
          setLoading(false);
          setLoadingMore(false);
        })
        .catch(error => {
          if (error.name === "AbortError") return;
          /* `call()` turned a 500 into `{results: [], count: 0}`, and `[]` is truthy — so a
             failed request rendered as "no photos found" with no way to tell the two apart.
             `getAbortable` throws, so a failure is a failure. */
          Alert.error(t("load_failed"));
          setLoading(false);
          setLoadingMore(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter, range, viewMode]
  );

  /* Filters apply as they change — but one tick of a multi-select fired a full request
     immediately, so ticking five points meant five of them in flight at once. Coalescing here
     rather than per control means every filter gets it, including the ones added later. */
  useEffect(() => {
    page.current = 1;
    const timer = setTimeout(() => fetchPage(1, false), FILTER_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      inflight.current?.abort();
    };
  }, [fetchPage]);

  /* Keep the address bar in step so the view can be shared, without adding history entries. */
  useEffect(() => {
    const search = filterToSearch(filter, range);
    if (search !== location.search.replace(/^\?/, "")) history.replace({ search });
    // location.search is read, not depended on: reacting to it would fight the write above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, range]);

  const visible = useMemo(() => (statusFilter === "all" ? data : data.filter(item => statusOf(item) === statusFilter)), [data, statusFilter]);

  const openIndex = openId == null ? -1 : visible.findIndex(item => item.id === openId);
  const openItem = openIndex >= 0 ? visible[openIndex] : null;

  /* ------------------------------------------------------------- mutations */

  /* The row is patched rather than refetched: one refetch to keep the grid honest would repaint
     it under the reviewer's cursor. */
  const patchLocal = (ids, isApproved) => {
    /* A Set, not `ids.includes` inside the map: bulk-approving a full page of 100 was 10,000
       comparisons. */
    const touched = new Set(ids);
    setData(prev =>
      prev.map(item =>
        touched.has(item.id)
          ? { ...item, approval: { ...item.approval, is_approved: isApproved, approved_by: me, approved_at: new Date().toISOString() } }
          : item
      )
    );
  };

  const postApproval = (item, isApproved) =>
    new Promise(resolve => {
      const info = item?.picsell_info;
      if (!info?.object_id || !info?.object_type) {
        resolve(false);
        return;
      }
      call(
        {
          method: "POST",
          url: "approvals/",
          post: { object_id: info.object_id, object_type: info.object_type, is_approved: isApproved, comment: "" },
        },
        response => resolve(Boolean(response))
      );
    });

  const decide = async (item, isApproved, advance) => {
    if (!item?.picsell_info?.object_id || !item?.picsell_info?.object_type) {
      Alert.error(translate(TRANSLATION_DOWNLOADER, "task_object_item_id_msg", lang));
      return;
    }
    /* Rejecting asks what to do about it: the reviewer marks up the photo and raises a corrective
       task, and the rejection is posted once that exists. "Reject only" inside the modal is the
       way out for a photo that needs no shelf work. Approving stays one click. */
    if (!isApproved) {
      setRejecting({ item, advance });
      return;
    }
    setBusyId(item.id);
    const ok = await postApproval(item, isApproved);
    setBusyId(null);
    if (!ok) return;
    patchLocal([item.id], isApproved);
    Alert.success(translate(TRANSLATION_DOWNLOADER, isApproved ? "task_accept_msg" : "task_reject_msg", lang));
    if (advance) step(1);
  };

  const bulkDecide = async isApproved => {
    const items = data.filter(item => selected.has(item.id));
    setBusyId("bulk");
    const results = await Promise.all(items.map(item => postApproval(item, isApproved)));
    setBusyId(null);
    const done = items.filter((_, i) => results[i]).map(item => item.id);
    if (done.length) patchLocal(done, isApproved);
    if (done.length < items.length) Alert.warning(translate(TRANSLATION_DOWNLOADER, "task_object_item_id_msg", lang));
    else Alert.success(translate(TRANSLATION_DOWNLOADER, isApproved ? "task_accept_msg" : "task_reject_msg", lang));
    setSelected(new Set());
  };

  /* --------------------------------------------------------------- queue nav */

  const step = delta => {
    if (!visible.length) {
      setOpenId(null);
      return;
    }
    /* After a decision the item may have left the filtered set, which makes "stay in place"
       the same thing as "move to the next one". */
    const from = openIndex < 0 ? 0 : openIndex;
    const next = (from + delta + visible.length) % visible.length;
    setOpenId(visible[next].id);
  };

  const closeModal = () => {
    setOpenId(null);
    if (routeId) history.replace({ pathname: "/photo-review", search: location.search });
  };

  /* ---------------------------------------------------------------- filters */

  const setSingle = (key, option) => {
    setChosen(prev => ({ ...prev, [key]: option }));
    setFilter(prev => ({ ...prev, [key]: option ? idOf(option) : "" }));
  };

  const setArray = (key, list) => setFilter(prev => ({ ...prev, [key]: list }));

  /* Names for values that arrived as bare ids — see `useLabelCache`. */
  const { labels, remember: rememberLabels, nameFor } = useLabelCache();

  /* One request per pause, not per keystroke (spec/03 — 250 ms). */
  const commentTimer = useRef(null);
  const [commentDraft, setCommentDraft] = useState(initial.filter.comment || "");
  const onComment = value => {
    setCommentDraft(value);
    clearTimeout(commentTimer.current);
    commentTimer.current = setTimeout(() => setFilter(prev => ({ ...prev, comment: value })), FILTER_DEBOUNCE_MS);
  };

  const resetFilters = () => {
    setFilter(blankFilter());
    setChosen({});
    setCommentDraft("");
    setRange(defaultRange());
    setStatusFilter("all");
    setSelected(new Set());
    setPreset(null);
    setPresetName("");
  };

  const clearChip = (key, value) => {
    if (key === "comment") {
      setCommentDraft("");
      setFilter(prev => ({ ...prev, comment: "" }));
      return;
    }
    if (SINGLE_KEYS.includes(key)) {
      setSingle(key, null);
      return;
    }
    setFilter(prev => ({ ...prev, [key]: (prev[key] || []).filter(v => String(idOf(v)) !== String(value)) }));
  };

  const chips = useMemo(() => {
    const out = [];
    if (range?.[0] && range?.[1]) {
      /* Short form, per spec/04 — `28 Jul → 11 Aug`. The year is only shown when the range
         leaves the current one, which is also what keeps the chip on one line on a phone. */
      const thisYear = new Date().getFullYear();
      const sameYear = range[0].year() === thisYear && range[1].year() === thisYear;
      const fmt = new Intl.DateTimeFormat(localeFor(lang), { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
      out.push({
        key: "period",
        label: translate(PHOTO_REPORT, "period", lang),
        value: `${fmt.format(range[0].toDate())} → ${fmt.format(range[1].toDate())}`,
      });
    }
    SINGLE_KEYS.forEach(key => {
      if (!filter[key]) return;
      /* `chosen[key]` is the option object the select handed back, so it names itself; falling
         back to the bare id sends `nameFor` to the resolution cache. */
      out.push({
        key,
        label: translate(ENTITY_DROPDOWN_SETTINGS, CHIP_LABEL[key], lang),
        value: nameFor(key, chosen[key] ?? filter[key]),
      });
    });
    ARRAY_KEYS.forEach(key => {
      (filter[key] || []).forEach(v =>
        out.push({
          key,
          id: idOf(v),
          label: translate(ENTITY_DROPDOWN_SETTINGS, CHIP_LABEL[key], lang),
          value: nameFor(key, v),
        })
      );
    });
    if (filter.comment) out.push({ key: "comment", label: translate(ENTITY_DROPDOWN_SETTINGS, "comment", lang), value: filter.comment });
    return out;
    /* `labels` is a dependency: a chip that arrived as an id has to re-render once its name lands. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, chosen, labels, range, lang]);

  /* ---------------------------------------------------------------- presets */

  const applyPreset = option => {
    setPreset(option);
    if (!option) {
      resetFilters();
      return;
    }
    let parsed = {};
    try {
      parsed = JSON.parse(option.filters_string) || {};
    } catch (error) {
      parsed = {};
    }
    // A saved filter may predate a field, so start from the full shape.
    setFilter({ ...blankFilter(), ...parsed, comment: parsed.comment || "" });
    setChosen({});
    setCommentDraft(parsed.comment || "");
    setPresetName(option.label || option.name || "");
  };

  const savePreset = () => {
    const name = presetName.trim();
    if (!name) {
      Alert.error(translate(TRANSLATION_DOWNLOADER, "valid_error_msg", lang), { position: "top" });
      return;
    }
    call({ method: "POST", url: "filters/", post: { name, is_active: true, filters_string: JSON.stringify(filter) } }, response => {
      if (!response?.id) {
        Alert.warning(translate(TRANSLATION_DOWNLOADER, "valid_error_msg", lang), { position: "top" });
        return;
      }
      Alert.success(t("filter_saved_msg"), { position: "top" });
      setPreset({ ...response, label: name });
      setPresetVersion(v => v + 1);
    });
  };

  const removePreset = () => {
    if (!preset?.id) {
      Alert.warning(translate(TRANSLATION_DOWNLOADER, "remove_confirmation_msg", lang), { position: "top" });
      return;
    }
    call({ method: "DELETE", url: `filters/${preset.id}/` }, response => {
      if (response?.ok !== true) return;
      Alert.success(translate(TRANSLATION_DOWNLOADER, "remove_success_msg", lang), { position: "top" });
      setPreset(null);
      setPresetName("");
      setPresetVersion(v => v + 1);
      resetFilters();
    });
  };

  /* --------------------------------------------------------------- download */

  const exportImages = urls => {
    if (!urls.length) return;
    setExporting(true);
    call({ method: "POST", url: "images/export/", post: { images: urls } }, () => setExporting(false), {}, true);
  };

  const firstPhotos = items => items.map(item => item.photo?.[0]).filter(Boolean);

  /* ------------------------------------------------------------- selection */

  const toggle = id =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () => {
    const allOn = visible.length > 0 && visible.every(item => selected.has(item.id));
    setSelected(allOn ? new Set() : new Set(visible.map(item => item.id)));
  };

  /* `React.memo` on the card only pays off if its handlers are referentially stable, and
     `toggle`/`decide` close over half the page's state. Rather than `useCallback` all the way
     down that chain, the current versions live in a ref and the card gets four handlers that
     never change identity. */
  const latest = useRef({});
  latest.current = { toggle, decide };

  const onCardToggle = useCallback(item => latest.current.toggle(item.id), []);
  const onCardOpen = useCallback(item => setOpenId(item.id), []);
  const onCardApprove = useCallback(item => latest.current.decide(item, true, false), []);
  const onCardReject = useCallback(item => latest.current.decide(item, false, false), []);

  /* ------------------------------------------------------------------ render */

  /* `<Form.Item label>` per spec/02, and the Form.Item IS the grid item. The Form is
     `component={false}` — there is nothing to submit here, filters apply as they change, and a
     real <form> would turn Enter in the comment box into a page reload. */
  const field = (labelKey, control, col = FIELD_COL) => (
    <Col key={labelKey} {...col}>
      <Form.Item label={translate(ENTITY_DROPDOWN_SETTINGS, labelKey, lang)}>{control}</Form.Item>
    </Col>
  );

  const selectedItems = data.filter(item => selected.has(item.id));

  return (
    <Flex vertical gap="middle" className="shell-content pr">
      <Flex className="pr-head" justify="space-between" align="flex-end" gap="middle" wrap>
        <Typography.Title level={1} className="pr-title">
          {translate(SIDE_NAV, "photo_report", lang)}
        </Typography.Title>
        <Space className="pr-head__actions" wrap>
          <Tooltip title={t("print_hint")}>
            <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
              {translate(TRANSLATION_DOWNLOADER, "export", lang)}
            </Button>
          </Tooltip>
          <Button type="primary" icon={<DownloadOutlined />} loading={exporting} disabled={!visible.length} onClick={() => exportImages(firstPhotos(visible))}>
            {translate(TRANSLATION_DOWNLOADER, "downloadAll", lang)}
          </Button>
        </Space>
      </Flex>

      <Card className="pr-filters" variant="outlined">
        <Form layout="vertical" component={false} colon={false}>
          <Row gutter={[12, 12]} className="pr-filters__grid">
            <Col {...FIELD_COL_WIDE}>
              <Form.Item label={t("period")}>
                <PeriodPicker value={range} onChange={setRange} maxDays={MAX_RANGE_DAYS} clearTo={defaultRange} />
              </Form.Item>
            </Col>

            {field(
              "region",
              <EntitySelect
                entity="county"
                value={filter.county}
                seed={chosen.county}
                prefix={<CompassOutlined />}
                onChange={o => setSingle("county", o)}
                onResolve={rememberLabels("county")}
              />
            )}
            {field(
              "network",
              <EntitySelect
                entity="network"
                value={filter.point__network}
                seed={chosen.point__network}
                prefix={<ClusterOutlined />}
                onChange={o => setSingle("point__network", o)}
                onResolve={rememberLabels("point__network")}
              />
            )}
            {field(
              "points",
              <EntitySelect
                entity="points"
                isMulti
                value={filter.points}
                county={filter.county}
                moduleFilter={filter.point__network}
                prefix={<PushpinOutlined />}
                onChange={list => setArray("points", list)}
                onResolve={rememberLabels("points")}
              />
            )}
            {field(
              "merchandisers",
              <EntitySelect
                entity="merchandisers"
                isMulti
                value={filter.users}
                prefix={<IdcardOutlined />}
                onChange={list => setArray("users", list)}
                onResolve={rememberLabels("users")}
              />
            )}
            {field(
              "clients",
              <EntitySelect
                entity="clients"
                value={filter.clients}
                seed={chosen.clients}
                prefix={<ShopOutlined />}
                onChange={o => setSingle("clients", o)}
                onResolve={rememberLabels("clients")}
              />
            )}
            {field(
              "comment",
              /* No placeholder: the field's own label already names it, and a repeat of the label
               inside the box reads as pre-filled text. */
              <Input value={commentDraft} prefix={<SearchOutlined />} allowClear onChange={event => onComment(event.target.value)} />
            )}
          </Row>

          {showExtra && (
            <Row gutter={[12, 12]} className="pr-filters__grid pr-filters__grid--extra">
              {field(
                "categories",
                <EntitySelect
                  entity="categories"
                  isMulti
                  value={filter.categories}
                  prefix={<AppstoreOutlined />}
                  onChange={list => setArray("categories", list)}
                  onResolve={rememberLabels("categories")}
                />
              )}
              {field(
                "subcategories",
                <EntitySelect
                  entity="subcategories"
                  isMulti
                  value={filter.subcategories}
                  prefix={<ApartmentOutlined />}
                  onChange={list => setArray("subcategories", list)}
                  onResolve={rememberLabels("subcategories")}
                />
              )}
              {field(
                "brands",
                <EntitySelect
                  entity="brands"
                  isMulti
                  value={filter.brands}
                  prefix={<TagOutlined />}
                  onChange={list => setArray("brands", list)}
                  onResolve={rememberLabels("brands")}
                />
              )}
              {field(
                "products",
                <EntitySelect
                  entity="products"
                  isMulti
                  value={filter.products}
                  prefix={<InboxOutlined />}
                  onChange={list => setArray("products", list)}
                  onResolve={rememberLabels("products")}
                />
              )}
              {field(
                "tags",
                <EntitySelect
                  entity="tags"
                  isMulti
                  value={filter.tags}
                  prefix={<TagsOutlined />}
                  onChange={list => setArray("tags", list)}
                  onResolve={rememberLabels("tags")}
                />
              )}
            </Row>
          )}

          <Flex vertical className="pr-presets">
            <Form.Item label={translate(ENTITY_DROPDOWN_SETTINGS, "custom_filter", lang)}>
              <Space wrap>
                {/* Remounted after a save/remove so the dropdown shows the change without its own
                cache-busting protocol. */}
                <EntitySelect
                  key={presetVersion}
                  className="pr-presets__pick"
                  entity="filters"
                  value={preset}
                  prefix={<BookOutlined />}
                  ariaLabel={translate(ENTITY_DROPDOWN_SETTINGS, "custom_filter", lang)}
                  onChange={applyPreset}
                />
                <Input
                  className="pr-presets__name"
                  value={presetName}
                  prefix={<BookOutlined />}
                  aria-label={translate(ENTITY_DROPDOWN_SETTINGS, "filter_name_placeholder", lang)}
                  placeholder={translate(ENTITY_DROPDOWN_SETTINGS, "filter_name_placeholder", lang)}
                  onChange={event => setPresetName(event.target.value)}
                />
                <Button size="small" icon={<SaveOutlined />} onClick={savePreset}>
                  {translate(ENTITY_DROPDOWN_SETTINGS, "filter_save", lang)}
                </Button>
                <Popconfirm
                  title={translate(TRANSLATION_DOWNLOADER, "confirmation_msg", lang)}
                  okText={translate(ENTITY_DROPDOWN_SETTINGS, "filter_remove", lang)}
                  cancelText={translate(TRANSLATION_DOWNLOADER, "cancel", lang)}
                  okButtonProps={{ danger: true }}
                  onConfirm={removePreset}>
                  <Button size="small" danger type="text" icon={<DeleteOutlined />}>
                    {translate(ENTITY_DROPDOWN_SETTINGS, "filter_remove", lang)}
                  </Button>
                </Popconfirm>
              </Space>
            </Form.Item>
          </Flex>

          <Flex className="pr-filters__foot" align="center" gap="small" wrap>
            {/* Removing the period chip returns the window to the default rather than clearing it
                — an unbounded query is exactly what the cap exists to prevent. */}
            <FilterChips
              items={chips}
              closeLabel={t("a_remove_filter")}
              onClose={(key, id) => (key === "period" ? setRange(defaultRange()) : clearChip(key, id))}
            />
            <Button type="text" size="small" icon={<SlidersOutlined />} aria-expanded={showExtra} onClick={() => setShowExtra(v => !v)}>
              {translate(ENTITY_DROPDOWN_SETTINGS, "additional_filters", lang)}
            </Button>
            <Button size="small" onClick={resetFilters}>
              {translate(ENTITY_DROPDOWN_SETTINGS, "filter_reset", lang)}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Flex className="pr-toolbar" align="center" gap="middle" wrap>
        <Segmented value={statusFilter} onChange={setStatusFilter} options={STATUS_SEGMENTS.map(([value, key]) => ({ value, label: t(key) }))} />
        <Segmented
          value={viewMode}
          onChange={value => {
            setViewMode(value);
            setSelected(new Set());
          }}
          options={[
            { value: "single", icon: <PictureOutlined />, label: translate(ENTITY_DROPDOWN_SETTINGS, "photo_review_type_single", lang) },
            { value: "group", icon: <CopyOutlined />, label: translate(ENTITY_DROPDOWN_SETTINGS, "photo_review_type_group", lang) },
          ]}
        />
        <Button type="text" size="small" icon={<CheckSquareOutlined />} disabled={!visible.length} onClick={toggleAll}>
          {t("select_all")}
        </Button>
      </Flex>

      {loading ? (
        <Row gutter={[16, 16]} className="pr-grid">
          {Array.from({ length: 8 }, (_, i) => (
            <Col key={i} {...CARD_COL}>
              <CardSkeleton />
            </Col>
          ))}
        </Row>
      ) : visible.length ? (
        <Row gutter={[16, 16]} className="pr-grid">
          {visible.map(item => (
            <Col key={item.id} {...CARD_COL}>
              <PhotoCard
                item={item}
                lang={lang}
                grouped={viewMode === "group"}
                isSelected={selected.has(item.id)}
                busy={busyId === item.id}
                onToggle={onCardToggle}
                onOpen={onCardOpen}
                onApprove={onCardApprove}
                onReject={onCardReject}
              />
            </Col>
          ))}
        </Row>
      ) : (
        <Empty
          className="pr-empty"
          image={<FileImageOutlined className="pr-empty__glyph-icon" />}
          description={
            <>
              <Typography.Title level={5} className="pr-empty__title">
                {t("empty_title")}
              </Typography.Title>
              <Typography.Text type="secondary" className="pr-empty__desc">
                {t("empty_desc")}
              </Typography.Text>
            </>
          }>
          <Button onClick={resetFilters}>{translate(ENTITY_DROPDOWN_SETTINGS, "filter_reset", lang)}</Button>
        </Empty>
      )}

      {/* Grouped mode paginates too. The mockup hides this in grouped view, but only because its
          data was local and complete; against the API a hidden pager caps the view at one page. */}
      {hasNext && !loading && (
        <Flex className="pr-loadmore" justify="center">
          <Button icon={<DownOutlined />} loading={loadingMore} onClick={() => fetchPage(page.current + 1, true)}>
            {t("load_more")}
          </Button>
        </Flex>
      )}

      {selected.size > 0 && (
        <Flex className="pr-bulkbar" role="region" aria-label={t("select_all")} align="center" gap="middle" wrap>
          <Typography.Text strong className="pr-bulkbar__n">
            {fill(t("w_selected"), { n: selected.size })}
          </Typography.Text>
          <Space className="pr-bulkbar__acts" wrap>
            <Button size="small" onClick={() => setSelected(new Set())}>
              {t("clear_selection")}
            </Button>
            <Button size="small" icon={<DownloadOutlined />} loading={exporting} onClick={() => exportImages(firstPhotos(selectedItems))}>
              {translate(TRANSLATION_DOWNLOADER, "download", lang)}
            </Button>
            <Button size="small" className="pr-btn--no" icon={<CloseCircleOutlined />} loading={busyId === "bulk"} onClick={() => bulkDecide(false)}>
              {translate(PHOTO_REVIEW, "reject_btn", lang)}
            </Button>
            <Button
              size="small"
              type="primary"
              className="pr-btn--ok"
              icon={<CheckCircleOutlined />}
              loading={busyId === "bulk"}
              onClick={() => bulkDecide(true)}>
              {translate(PHOTO_REVIEW, "approv_btn", lang)}
            </Button>
          </Space>
        </Flex>
      )}

      {openItem && !rejecting && (
        <ReviewModal
          item={openItem}
          position={openIndex + 1}
          total={visible.length}
          lang={lang}
          busy={busyId === openItem.id}
          onClose={closeModal}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
          onApprove={() => decide(openItem, true, true)}
          onReject={() => decide(openItem, false, true)}
        />
      )}

      {rejecting && (
        <RejectTaskModal
          /* The photo report's own shape, mapped at the call site now that the modal is shared:
             `picsell_info` is where this endpoint carries the approvable object's identity. */
          target={{ objectId: rejecting.item.picsell_info?.object_id, objectType: rejecting.item.picsell_info?.object_type }}
          photo={rejecting.item.photo?.[0]}
          point={rejecting.item.visit?.point}
          visitId={rejecting.item.visit_id}
          lang={lang}
          onCancel={() => setRejecting(null)}
          onDone={() => {
            const { item, advance } = rejecting;
            patchLocal([item.id], false);
            setRejecting(null);
            if (advance) step(1);
          }}
        />
      )}
    </Flex>
  );
}
